/**
 * MText to MLeader conversion utilities.
 *
 * Converts standalone MText entities to MLeader annotations
 * while preserving text content and position.
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import {
  openAs,
  findEntityByHandle,
  getEntityHandle,
  getCurrentSpaceId,
} from "../core/oda";
import {
  OpenMode,
  TextAttachmentType,
  LeaderDirectionType,
  PropertyOverrideType,
} from "../core/constants";
import { ensureDRMLeaderStyle, getTextStyleName } from "./mleaderStyle";
import { getEntityLayerName } from "../entities";
import {
  getExplicitColorValue,
  buildOdCmColor,
  type ExplicitColorValue,
} from "../colors/color";
import type { Point3D } from "../types/types";

export type { Point3D };

/**
 * Options for creating an MLeader from MText.
 */
export interface CreateMLeaderFromMTextOptions {
  /** Text content */
  textContent: string;
  /** Text height */
  textHeight: number;
  /** Text location (attachment point of text) */
  textLocation: Point3D;
  /** Text extent (bounding box) */
  textExtent: { min: Point3D; max: Point3D };
  /** Layer name for the MLeader */
  layerName: string;
  /** Leader line vertices: [landingPoint, doglegPoint, arrowTip] */
  vertices: [Point3D, Point3D, Point3D];
  /** Text style handle from the original MText entity */
  textStyleHandle?: string;
  /** Attachment point (1-9 for MText) */
  attachmentPoint?: number;
  /** Explicit entity color of the original MText (absent when ByLayer/ByBlock) */
  entityColor?: ExplicitColorValue;
}

/**
 * Create an MLeader from MText properties.
 *
 * @param viewer - The drawing viewer
 * @param options - Creation options
 * @returns The created MLeader entity ID, or null on failure
 */
export function createMLeaderFromMText(
  viewer: DRDrawingViewer,
  options: CreateMLeaderFromMTextOptions,
): DrawingModule.OdDbObjectId | null {
  const {
    textContent,
    textHeight,
    textLocation,
    textExtent,
    layerName,
    vertices,
    textStyleHandle,
    attachmentPoint,
    entityColor,
  } = options;
  const module = viewer.module;
  const db = viewer.database;

  if (!db) {
    console.error("[Conversion] No database available");
    return null;
  }

  // Resolve text style handle to OdDbObjectId
  const textStyleId = textStyleHandle
    ? findEntityByHandle(module, db, textStyleHandle)
    : null;

  //const [_landingPoint, _doglegPoint, arrowTip] = vertices;
  const arrowTip = vertices[2];

  const currentSpaceId = getCurrentSpaceId(db);
  const currentSpace = openAs(
    currentSpaceId,
    module.OdDbBlockTableRecord,
    OpenMode.kForWrite,
  );

  // Create MLeader
  const mleader = module.OdDbMLeader.createObject();
  mleader.setDatabaseDefaults(db, true);

  // Set scale to 1 — kept as entity override, workaround for drawings with stray scale values.
  mleader.setScale(1);

  // Compute visual dimensions and resolve text style name for DR style key.
  const arrowSize = 0.6 * textHeight;
  // Preview uses: landingGap = 0.5 * textHeight, doglegGap = 1.5 * textHeight
  // So doglegLength = doglegGap - landingGap = 1.0 * textHeight
  const landingGap = 0.5 * textHeight;
  const doglegLength = 1.0 * textHeight;

  // Create or find the DR MLeaderStyle for this (textStyle × textHeight) pair.
  const drStyleId = ensureDRMLeaderStyle(module, db, {
    textStyleName: getTextStyleName(module, textStyleId),
    textStyleId,
    textHeight,
    arrowSize,
    landingGap,
    doglegLength,
  });
  mleader.setMLeaderStyle(drStyleId);
  deleteObj(drStyleId);

  // Set entity-level values then clear all corresponding override flags so
  // AutoCAD inherits from the DR style (same pattern as creation.ts).
  mleader.setContentType(2); // kMTextContent
  mleader.setArrowSize(arrowSize);
  mleader.setEnableDogleg(true);
  mleader.setEnableLanding(true);
  mleader.setLandingGap(landingGap);
  mleader.setDoglegLength1(doglegLength);

  // Create and set the MText content
  if (textContent) {
    const mtext = module.OdDbMText.createObject();
    mtext.setDatabaseDefaults(db, true);

    const textStr = new module.OdString(textContent);
    mtext.setContents(textStr);
    deleteObj(textStr);

    // Preserve attachment point from original MText
    if (attachmentPoint !== undefined) {
      mtext.setAttachment(attachmentPoint);
    }

    // Set text location
    const textLoc = new module.OdGePoint3d(
      textLocation.x,
      textLocation.y,
      textLocation.z,
    );
    mtext.setLocation(textLoc);
    deleteObj(textLoc);

    mleader.setMText(mtext);
    deleteObj(mtext);
  }

  // Apply text style from original MText entity, then set text height.
  if (textStyleId) {
    mleader.setTextStyleId(textStyleId);
  }
  mleader.setTextHeight(textHeight);

  // Clear all override flags covered by the DR style so AutoCAD inherits from it.
  const OPT = PropertyOverrideType;
  mleader.setOverride(OPT.kTextStyleId, false);
  mleader.setOverride(OPT.kContentType, false);
  mleader.setOverride(OPT.kTextHeight, false);
  mleader.setOverride(OPT.kArrowSize, false);
  mleader.setOverride(OPT.kEnableDogleg, false);
  mleader.setOverride(OPT.kEnableLanding, false);
  mleader.setOverride(OPT.kLandingGap, false);
  mleader.setOverride(OPT.kDoglegLength, false);
  mleader.setOverride(OPT.kTextLeftAttachmentType, false);
  mleader.setOverride(OPT.kTextRightAttachmentType, false);

  // Determine which side the leader should attach based on arrow position
  const textCenterX = (textExtent.min.x + textExtent.max.x) / 2;
  const attachRight = arrowTip.x >= textCenterX;

  // Set text attachment type for both left and right leaders (centered vertically)
  mleader.setTextAttachmentType(
    TextAttachmentType.kAttachmentMiddle,
    LeaderDirectionType.kLeftLeader,
  );
  mleader.setTextAttachmentType(
    TextAttachmentType.kAttachmentMiddle,
    LeaderDirectionType.kRightLeader,
  );

  // Add leader line using addLeaderLine1 (with point) - this reliably creates leader lines
  const arrowPoint = new module.OdGePoint3d(arrowTip.x, arrowTip.y, arrowTip.z);
  const leaderLineIdx = new module.Int(0); // output parameter
  const result = mleader.addLeaderLine1(
    arrowPoint,
    leaderLineIdx as unknown as number,
  );
  deleteObj(arrowPoint);

  if (result !== 0) {
    console.warn("[createMLeaderFromMText] addLeaderLine1 returned:", result);
  }

  // Force the correct dogleg direction based on arrow position
  const doglegDir = new module.OdGeVector3d(attachRight ? 1 : -1, 0, 0);
  mleader.setDoglegDirection(0, doglegDir);
  deleteObj(doglegDir);

  deleteObj(leaderLineIdx);

  // rest text location on the MLeader as it can be moved
  const textLocationPoint = new module.OdGePoint3d(
    textLocation.x,
    textLocation.y,
    textLocation.z,
  );

  mleader.setTextLocation(textLocationPoint);
  deleteObj(textLocationPoint);

  // Set layer
  const layerStr = new module.OdString(layerName);
  mleader.setLayer(layerStr, false, true);
  deleteObj(layerStr);

  // Carry over the MText's own color (a text colored ByLayer stays ByLayer).
  // Entity color drives the leader lines/arrow (ByBlock in the DR style);
  // the explicit text color override keeps the text identical to the original,
  // whatever the style's text color resolves to.
  if (entityColor) {
    const colorObj = buildOdCmColor(module, entityColor);
    mleader.setColor(colorObj, true);
    mleader.setTextColor(colorObj);
    deleteObj(colorObj);
  }

  // Append to model space
  const mleaderId = currentSpace.appendOdDbEntity(mleader);

  deleteObj(mleader);
  deleteObj(currentSpace);

  return mleaderId;
}

/**
 * Common text properties used by both MText and MLeader.
 */
export interface TextEntityProperties {
  textContent: string;
  textHeight: number;
  textLocation: Point3D;
  textExtent: { min: Point3D; max: Point3D };
  layerName: string;
  /** Whether this is an MLeader (vs standalone MText) */
  isMLeader: boolean;
  /** Text style handle from the entity */
  textStyleHandle?: string;
  /** Attachment point (1-9 for MText) */
  attachmentPoint?: number;
  /** Explicit entity color (absent when ByLayer/ByBlock) */
  entityColor?: ExplicitColorValue;
}

/**
 * Get MText properties needed for conversion.
 *
 * @param viewer - The drawing viewer
 * @param entityId - The MText entity ID
 * @returns MText properties or null if not an MText
 */
export function getMTextProperties(
  viewer: DRDrawingViewer,
  entityId: DrawingModule.OdDbObjectId,
): TextEntityProperties | null {
  const module = viewer.module;

  const pObj = entityId.safeOpenObject(OpenMode.kForRead, false);
  const mtext = module.OdDbMText.cast(pObj);
  deleteObj(pObj);

  if (!mtext) {
    // Not an MText - caller should try getMLeaderProperties
    return null;
  }

  // Get text content
  const contentsStr = mtext.contents();
  const textContent = contentsStr.toString();
  deleteObj(contentsStr);

  // Get text height
  const textHeight = mtext.textHeight();

  // Get text style handle
  const textStyleObjId = mtext.textStyle();
  const textStyleHandle = getEntityHandle(textStyleObjId);
  deleteObj(textStyleObjId);

  // Get text location
  const location = mtext.location();
  const textLocation: Point3D = { x: location.x, y: location.y, z: location.z };
  deleteObj(location);

  // Get text extent
  const extents = new module.OdGeExtents3d();
  mtext.getGeomExtents(extents);
  const minPt = extents.minPoint();
  const maxPt = extents.maxPoint();
  const textExtent = {
    min: { x: minPt.x, y: minPt.y, z: minPt.z },
    max: { x: maxPt.x, y: maxPt.y, z: maxPt.z },
  };
  deleteObj(extents);

  // Get attachment point
  const attachmentPoint = mtext.attachment();

  // Get the entity's own color, if any (undefined when ByLayer/ByBlock)
  const entityColor = getExplicitColorValue(mtext.color());

  // Get layer name
  const layerName = getEntityLayerName(module, entityId);
  deleteObj(mtext);

  return {
    textContent,
    textHeight,
    textLocation,
    textExtent,
    layerName,
    isMLeader: false,
    textStyleHandle,
    attachmentPoint,
    entityColor,
  };
}

/**
 * Get MLeader properties for adding leader lines.
 *
 * @param viewer - The drawing viewer
 * @param entityId - The MLeader entity ID
 * @returns MLeader text properties or null if not an MLeader
 */
export function getMLeaderProperties(
  viewer: DRDrawingViewer,
  entityId: DrawingModule.OdDbObjectId,
): TextEntityProperties | null {
  const module = viewer.module;

  const pObj = entityId.safeOpenObject(OpenMode.kForRead, false);
  const mleader = module.OdDbMLeader.cast(pObj);
  deleteObj(pObj);

  if (!mleader) {
    // Not an MLeader
    return null;
  }

  // Get text height
  const textHeight = mleader.textHeight();

  // Get text style handle
  const textStyleObjId = mleader.textStyleId();
  const textStyleHandle = getEntityHandle(textStyleObjId);
  deleteObj(textStyleObjId);

  // Get text location (getTextLocation uses output parameter)
  const textLoc = new module.OdGePoint3d(0, 0, 0);
  mleader.getTextLocation(textLoc);
  const textLocation: Point3D = { x: textLoc.x, y: textLoc.y, z: textLoc.z };
  deleteObj(textLoc);

  // Get MText content and extent from embedded MText
  let textContent = "";
  let textExtent: { min: Point3D; max: Point3D };

  const mtext = mleader.mtext();
  if (mtext) {
    const contentsStr = mtext.contents();
    textContent = contentsStr.toString();
    deleteObj(contentsStr);

    // Get the real bounding box of the embedded MText
    const mtextExtents = new module.OdGeExtents3d();
    mtext.getGeomExtents(mtextExtents);
    const minPt = mtextExtents.minPoint();
    const maxPt = mtextExtents.maxPoint();
    textExtent = {
      min: { x: minPt.x, y: minPt.y, z: minPt.z },
      max: { x: maxPt.x, y: maxPt.y, z: maxPt.z },
    };
    deleteObj(mtextExtents);
    deleteObj(mtext);
  } else {
    // Fallback: estimate text extent based on text location and height
    const estimatedWidth = textHeight * Math.max(textContent.length * 0.6, 2);
    textExtent = {
      min: { x: textLocation.x, y: textLocation.y - textHeight * 0.5, z: 0 },
      max: {
        x: textLocation.x + estimatedWidth,
        y: textLocation.y + textHeight * 0.5,
        z: 0,
      },
    };
  }

  // Get layer name
  const layerName = getEntityLayerName(module, entityId);
  deleteObj(mleader);

  return {
    textContent,
    textHeight,
    textLocation,
    textExtent,
    layerName,
    isMLeader: true,
    textStyleHandle,
  };
}

/**
 * Get text properties from either MText or MLeader entity.
 *
 * @param viewer - The drawing viewer
 * @param entityId - The entity ID (MText or MLeader)
 * @returns Text properties or null if not a text entity
 */
export function getTextEntityProperties(
  viewer: DRDrawingViewer,
  entityId: DrawingModule.OdDbObjectId,
): TextEntityProperties | null {
  // Try MText first
  const mtextProps = getMTextProperties(viewer, entityId);
  if (mtextProps) return mtextProps;

  // Try MLeader
  const mleaderProps = getMLeaderProperties(viewer, entityId);
  if (mleaderProps) return mleaderProps;

  return null;
}

/**
 * Add a leader line to an existing MLeader.
 *
 * Uses addLeaderLine1 (point-based) which reliably creates new lines,
 * but preserves text location to prevent it from shifting to origin.
 *
 * @param viewer - The drawing viewer
 * @param entityId - The MLeader entity ID
 * @param arrowTip - Position for the arrow tip
 * @returns True if successful
 */
export function addLeaderLineToMLeader(
  viewer: DRDrawingViewer,
  entityId: DrawingModule.OdDbObjectId,
  arrowTip: Point3D,
): boolean {
  const module = viewer.module;
  const mleader = openAs(entityId, module.OdDbMLeader, OpenMode.kForWrite);

  if (!mleader) {
    console.warn("[Conversion] Entity is not MLeader");
    return false;
  }

  // Add a new leader line using addLeaderLine1 (with point) - this reliably creates new lines
  const arrowPoint = new module.OdGePoint3d(arrowTip.x, arrowTip.y, arrowTip.z);
  const leaderLineIdx = new module.Int(0); // output parameter
  const result = mleader.addLeaderLine1(
    arrowPoint,
    leaderLineIdx as unknown as number,
  );
  deleteObj(arrowPoint);
  deleteObj(leaderLineIdx);
  deleteObj(mleader);

  if (result !== 0) {
    console.warn("[Conversion] addLeaderLine1 returned:", result);
    return false;
  }

  return true;
}

/**
 * Create a standalone MText entity from an MLeader's text properties.
 *
 * Extracts text content, height, location, and layer from the MLeader
 * and creates an equivalent standalone MText in model space.
 *
 * @param viewer - The drawing viewer
 * @param mleaderId - The MLeader entity ID to extract text from
 * @returns The created MText entity ID, or null on failure
 */
export function createMTextFromMLeader(
  viewer: DRDrawingViewer,
  mleaderId: DrawingModule.OdDbObjectId,
): DrawingModule.OdDbObjectId | null {
  const module = viewer.module;
  const db = viewer.database;
  if (!db) return null;

  const pObj = mleaderId.safeOpenObject(OpenMode.kForRead, false);
  const mleader = module.OdDbMLeader.cast(pObj);
  deleteObj(pObj);
  if (!mleader) return null;

  // Extract text properties from embedded MText
  const embeddedMText = mleader.mtext();
  if (!embeddedMText) {
    deleteObj(mleader);
    return null;
  }

  const contentsStr = embeddedMText.contents();
  const textContent = contentsStr.toString();
  deleteObj(contentsStr);

  const textHeight = embeddedMText.textHeight();

  // Get the location from the embedded MText
  const loc = embeddedMText.location();
  const textLocation = { x: loc.x, y: loc.y, z: loc.z };
  deleteObj(loc);

  // Get the attachment point from the embedded MText
  const attachmentPoint = embeddedMText.attachment();

  deleteObj(embeddedMText);

  // Get text style from MLeader
  const mleaderTextStyleId = mleader.textStyleId();

  // Get layer name (read from entity before closing mleader)
  const layerName = getEntityLayerName(module, mleaderId);

  deleteObj(mleader);

  // Create standalone MText
  const currentSpaceId = getCurrentSpaceId(db);
  const currentSpace = openAs(
    currentSpaceId,
    module.OdDbBlockTableRecord,
    OpenMode.kForWrite,
  );

  const mtext = module.OdDbMText.createObject();
  mtext.setDatabaseDefaults(db, true);

  const textStr = new module.OdString();
  textStr.set(textContent);
  mtext.setContents(textStr);
  deleteObj(textStr);

  // Preserve text style from the original MLeader
  if (mleaderTextStyleId) {
    mtext.setTextStyle(mleaderTextStyleId);
    deleteObj(mleaderTextStyleId);
  }
  mtext.setTextHeight(textHeight);

  // Preserve the attachment point from the embedded MText
  mtext.setAttachment(attachmentPoint);

  const locationPt = new module.OdGePoint3d(
    textLocation.x,
    textLocation.y,
    textLocation.z,
  );
  mtext.setLocation(locationPt);
  deleteObj(locationPt);

  const layerStr = new module.OdString();
  layerStr.set(layerName);
  mtext.setLayer(layerStr, false, true);
  deleteObj(layerStr);

  const mtextId = currentSpace.appendOdDbEntity(mtext);

  deleteObj(mtext);
  deleteObj(currentSpace);

  return mtextId;
}

/**
 * Calculate leader vertices based on text extent and arrow tip position.
 *
 * The leader has 3 vertices:
 * 1. Landing point - horizontal from text edge
 * 2. Dogleg point - where the leader bends
 * 3. Arrow tip - user-specified position
 *
 * Side detection: If arrowTip.x >= textCenterX, attach from right edge, else left edge.
 *
 * @param textExtent - Text bounding box
 * @param textHeight - Text height for scaling gaps
 * @param arrowTip - User-specified arrow tip position
 * @returns Tuple of [landingPoint, doglegPoint, arrowTip]
 */
export function calculateLeaderVertices(
  textExtent: { min: Point3D; max: Point3D },
  textHeight: number,
  arrowTip: Point3D,
): [Point3D, Point3D, Point3D] {
  const textCenterX = (textExtent.min.x + textExtent.max.x) / 2;
  const textCenterY = (textExtent.min.y + textExtent.max.y) / 2;

  // Determine which side to attach the leader
  const attachRight = arrowTip.x >= textCenterX;

  // Calculate landing point (0.5 * textHeight from text edge)
  const landingGap = 0.5 * textHeight;
  const landingPoint: Point3D = attachRight
    ? { x: textExtent.max.x + landingGap, y: textCenterY, z: 0 }
    : { x: textExtent.min.x - landingGap, y: textCenterY, z: 0 };

  // Calculate dogleg point (1.5 * textHeight from text edge)
  const doglegGap = 1.5 * textHeight;
  const doglegPoint: Point3D = attachRight
    ? { x: textExtent.max.x + doglegGap, y: textCenterY, z: 0 }
    : { x: textExtent.min.x - doglegGap, y: textCenterY, z: 0 };

  return [landingPoint, doglegPoint, arrowTip];
}
